import { useCallback, useEffect, useMemo } from "react";
import { FieldErrors, Resolver, useForm, UseFormRegister } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { isValidEmail } from "../../../../../../../../common/validations/validationsForm";
import { IEmailItem, IEmailItemWithIndex } from "../../../../../../../../models";
import { useAppDispatch } from "../../../../../../../../redux/hooks";
import {
    addAdminEmailItem,
    removeAdminEmailItem,
    updateAdminEmailItem,
    updateAdminEmailItemIsValid,
} from "../../../../../../../../redux/reducers/user/profile/userProfile";
import { compileNameOfProperty } from "../../../../../../../../utils";
import { CustomIcons } from "../../../../../../../common/customIcons/customIcons";
import { FormField } from "../../../../../../../common/fields/fields";
import { UserProfileAdminEmailItemStyle } from "./itemStyle.jss";
import { generateRandomKeys } from "../../../../../../../../utils/keys";

interface IProps {
    email: IEmailItem;
    isLastIndex: boolean;
    isUniqueIndex: boolean;
    index: number;
}

export const UserProfileAdminEmailItem: React.FC<IProps> = ({ email, isLastIndex, isUniqueIndex, index }) => {
    const style = UserProfileAdminEmailItemStyle();
    const dispatch = useAppDispatch();
    const { t } = useTranslation("common");

    const formFieldKey = useMemo(() => generateRandomKeys(), []);

    const emailResolver: Resolver<IEmailItem> = async (values: IEmailItem) => {
        let errors = {};
        Object.entries(values).forEach(([name, value]) => {
            if (name === compileNameOfProperty<IEmailItem>("email")) {
                if (!isValidEmail(value)) {
                    errors = {
                        ...errors,
                        [name]: {
                            type: "required",
                            message: "FORM.VALIDATION.EMAIL",
                        },
                    };
                }
            }
        });
        return { values, errors };
    };
    const {
        register,
        formState: { errors: validationErrors, isValid },
    } = useForm<any>({ mode: "onChange", resolver: emailResolver });

    useEffect(() => {
        const payload: IEmailItemWithIndex = {
            index: index,
            email: email.email,
            isValid,
        };
        dispatch(updateAdminEmailItemIsValid(payload));
    }, [isValid]);

    const onInputChange = useCallback(
        (event) => {
            const payload: IEmailItemWithIndex = {
                index: index,
                email: event?.target?.value,
                isValid,
            };
            dispatch(updateAdminEmailItem(payload));
        },
        [dispatch],
    );

    const onRemoveItem = () => {
        dispatch(removeAdminEmailItem(index));
    };

    const onAddItem = () => {
        dispatch(addAdminEmailItem());
    };

    const emailRegister: UseFormRegister<IEmailItem> = register;
    const emailErrors: FieldErrors<IEmailItem> = validationErrors;

    return (
        <>
            <div className={style.emailContainer}>
                <div className={style.emailContent}>
                    <FormField
                        key={formFieldKey}
                        type="text"
                        label={t("USER.SIGNUP.EMAIL")}
                        value={email.email}
                        error={emailErrors.email}
                        {...emailRegister(compileNameOfProperty<IEmailItem>("email"), {
                            onChange: onInputChange,
                            value: email.email,
                        })}
                        isRequired={true}
                        autocomplete={"none"}
                    />
                    {!isUniqueIndex && (
                        <CustomIcons
                            iconName="Cancel"
                            onClick={onRemoveItem}
                            size={14}
                        />
                    )}
                </div>
                {isLastIndex && (
                    <div className={style.addEmail}>
                        <CustomIcons
                            iconName="Add"
                            onClick={onAddItem}
                            size={14}
                        />
                    </div>
                )}
            </div>
        </>
    );
};
